import { useEffect, useState } from "react";
import { Lock, Save, CheckCircle2 } from "lucide-react";
import { api, formatApiError, useAuth } from "@/lib/api";
import { toast } from "sonner";

export default function StagiaireProfile() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        adeli_rpps: "",
    });
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPwd, setSavingPwd] = useState(false);
    const [notify, setNotify] = useState(true);

    useEffect(() => {
        if (user) {
            setForm({
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                phone: user.phone || "",
                adeli_rpps: user.adeli_rpps || "",
            });
        }
    }, [user]);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await api.put("/stagiaire/profile", form);
            await refresh();
            toast.success("Profil mis à jour");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingProfile(false);
        }
    };

    const savePwd = async (e) => {
        e.preventDefault();
        if (pwd.next !== pwd.confirm) {
            toast.error("Les mots de passe ne correspondent pas");
            return;
        }
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwd.current,
                new_password: pwd.next,
            });
            toast.success("Mot de passe mis à jour");
            setPwd({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingPwd(false);
        }
    };

    return (
        <div className="mx-auto max-w-3xl px-6 py-10" data-testid="page-stagiaire-profile">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">Mon profil</div>
                <h1 className="mt-3 font-display text-5xl font-normal tracking-tightest">Mes informations.</h1>
            </div>

            <form onSubmit={saveProfile} className="mt-10 rounded-2xl border border-brand-line bg-white p-8 shadow-premium space-y-5" data-testid="form-profile">
                <h2 className="font-display text-2xl">Informations personnelles</h2>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Prénom</label>
                        <input
                            data-testid="profile-first-name"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                            value={form.first_name}
                            onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Nom</label>
                        <input
                            data-testid="profile-last-name"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                            value={form.last_name}
                            onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider">Email</label>
                    <input
                        disabled
                        className="mt-2 w-full rounded-xl border border-brand-line bg-[#FAFAFA] px-4 py-3 text-brand-smoke outline-none"
                        value={user?.email || ""}
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider">Téléphone</label>
                    <input
                        data-testid="profile-phone"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider">Numéro ADELI / RPPS</label>
                    <input
                        data-testid="profile-adeli"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        value={form.adeli_rpps}
                        onChange={(e) => setForm((f) => ({ ...f, adeli_rpps: e.target.value }))}
                        placeholder="Optionnel — utile pour les prises en charge FIF PL &amp; DPC"
                    />
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-brand-line bg-[#FAFAFA] p-4">
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-1 h-4 w-4 accent-brand-orange" />
                    <span className="text-sm text-brand-ink/85">
                        Recevoir des notifications par email pour mes prochaines sessions et mes documents
                        <span className="block text-xs text-brand-smoke">(notifications actives en Phase 4)</span>
                    </span>
                </label>

                <div className="flex justify-end pt-2">
                    <button type="submit" disabled={savingProfile} data-testid="profile-save" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                        <Save className="h-4 w-4" /> {savingProfile ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </div>
            </form>

            <form onSubmit={savePwd} className="mt-8 rounded-2xl border border-brand-line bg-white p-8 shadow-premium space-y-5" data-testid="form-change-password">
                <h2 className="font-display text-2xl">Changer mon mot de passe</h2>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider">Mot de passe actuel</label>
                    <input
                        type="password"
                        required
                        data-testid="profile-current-password"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        value={pwd.current}
                        onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider">Nouveau mot de passe</label>
                    <input
                        type="password"
                        required
                        data-testid="profile-new-password"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        value={pwd.next}
                        onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider">Confirmer</label>
                    <input
                        type="password"
                        required
                        data-testid="profile-confirm-password"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        value={pwd.confirm}
                        onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                    />
                </div>
                <div className="flex justify-end pt-2">
                    <button type="submit" disabled={savingPwd} data-testid="profile-change-password" className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-semibold text-white hover:bg-brand-coal disabled:opacity-60">
                        <Lock className="h-4 w-4" /> {savingPwd ? "Mise à jour…" : "Changer le mot de passe"}
                    </button>
                </div>
            </form>
        </div>
    );
}
